import React from 'react';
import { type TableProps } from 'antd';
import { IOrder, IOrderItem } from '@/interfaces/order';
import TableComponent from '@/components/table';
import { formatNumber } from '@/utils/dataUtils';
import { round } from 'lodash';
import { mediaView } from '@/utils/mediaUtils';
import { twMerge } from 'tailwind-merge';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';

interface IProps {
  order: Partial<IOrder>;
  orderItems: Partial<IOrderItem>[];
  showFooter?: boolean;
}

const OrderItemTable = ({ orderItems, order, showFooter = true }: IProps) => {
  const columns: TableProps<Partial<IOrderItem>>['columns'] = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 130,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'เลขที่ใบเสนอราคา',
      dataIndex: 'qtNo',
      width: 140,
      align: 'center',
      render: (_: number, rec) => (
        <div className="flex flex-row gap-3 cursor-pointer justify-center">
          <div
            className={twMerge(
              'flex items-center gap-1',
              !rec.qtNo && 'text-gray-400',
              rec.qtId && 'text-blue-600 underline',
            )}
            onClick={() => (rec.qtId ? mediaView({ id: rec.qtId }) : false)}
          >
            {_ ?? ''}
            {rec.qtId && (
              <ArrowTopRightOnSquareIcon className="w-4 h-4 text-blue-600" />
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'COA',
      dataIndex: 'coa',
      key: 'coa',
      align: 'center',
      width: 220,
      render: (_, rec) => (
        <div
          className={twMerge(
            'flex gap-1 items-center',
            rec.coaId && 'text-blue-600 underline',
            'cursor-pointer',
          )}
          onClick={() => (rec.coaId ? mediaView({ id: rec.coaId }) : false)}
        >
          {_ ?? 'ไม่พบไฟล์ COA'}
          {rec.coaId && (
            <ArrowTopRightOnSquareIcon className="w-4 h-4 text-blue-600" />
          )}
        </div>
      ),
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec) =>
        formatNumber(
          round(Number(rec?.quantity) / Number(rec?.conversion), 2),
          2,
        ),
    },
    {
      title: 'หน่วย Package',
      width: 120,
      dataIndex: 'packageUnitName',
    },
    {
      title: 'จำนวน',
      width: 120,
      dataIndex: 'quantity',
      align: 'right',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'หน่วยขาย',
      width: 100,
      dataIndex: 'baseUnitName',
      className: 'font-bold',
    },
    {
      title: 'ราคา',
      width: 120,
      dataIndex: 'unitPrice',
      align: 'right',
      className: 'font-bold text-green-700',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'รวมเงิน',
      width: 120,
      dataIndex: 'totalPrice',
      align: 'right',
      fixed: 'right',
      className: 'font-bold',
      render: (_: number, rec) =>
        formatNumber(round(Number(rec.quantity) * Number(rec.unitPrice), 2), 2),
    },
  ];

  let totalPrice = 0;
  let summaryPrice = 0;
  orderItems.forEach(({ quantity, unitPrice }) => {
    totalPrice += Number(round((quantity || 1) * (unitPrice || 0), 2));
  });
  const vatPrice = round(
    (Number(totalPrice - Number(order.discountAmount || 0)) *
      Number(order?.vatRate)) /
      100,
    2,
  );
  summaryPrice =
    Number(totalPrice - Number(order.discountAmount || 0)) + vatPrice;

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<Partial<IOrderItem>>
        columns={columns}
        criteria={{}}
        data={{
          data: orderItems,
          totalRecords: 0,
        }}
        loading={false}
        onTableChange={() => {
          console.log();
        }}
        scrollLength={1500}
        showPagination={false}
      />
      {showFooter && (
        <div className="flex justify-end">
          <div className="flex flex-col border border-red-400 rounded-lg p-2 text-bold w-[290px] bg-red-50">
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]  text-sm"
              label="รวมเงิน"
              detail={formatNumber(totalPrice, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]    text-sm"
              label="ส่วนลด"
              detail={formatNumber(Number(order?.discountAmount), 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]    text-sm"
              label="รวมเงินหลังหักส่วนลด"
              detail={formatNumber(
                Number(totalPrice - Number(order.discountAmount || 0)),
                2,
              )}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]    text-sm"
              label={`${order?.vatRate} %`}
              detail={formatNumber(vatPrice, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]   text-sm"
              label="ยอดสุทธิ"
              detail={formatNumber(summaryPrice, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderItemTable;
